<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { content, attachments, contentAttributes, messageType } =
  useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const hasAttachments = computed(
  () => Array.isArray(attachments.value) && attachments.value.length > 0
);

/** Text / captions only; media and files render in a separate bubble below */
const showTextBubble = computed(() => {
  if (isEmpty.value && !hasAttachments.value) return true;
  if (renderContent.value) return true;
  if (hasTranslations.value) return true;
  if (isTemplate.value && contentAttributes.value?.submittedEmail) return true;
  return false;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <div class="flex min-h-0 min-w-0 flex-col gap-2 self-stretch">
    <BaseBubble
      v-if="showTextBubble"
      class="max-w-full flex-col px-3 py-1.5"
      :class="
        hasAttachments ? 'inline-flex' : 'flex min-h-0 w-full min-w-0 flex-1'
      "
      data-bubble-name="text"
    >
      <div
        class="flex min-h-0 flex-col gap-1.5"
        :class="!hasAttachments ? 'flex-1 justify-center' : undefined"
      >
        <span v-if="isEmpty" class="text-n-slate-11">
          {{ $t('CONVERSATION.NO_CONTENT') }}
        </span>
        <FormattedContent v-if="renderContent" :content="renderContent" />
        <TranslationToggle
          v-if="hasTranslations"
          class="shrink-0"
          :showing-original="renderOriginal"
          @toggle="handleSeeOriginal"
        />
        <template v-if="isTemplate">
          <div
            v-if="contentAttributes.submittedEmail"
            class="rounded-lg bg-n-alpha-3 px-2 py-1"
          >
            {{ contentAttributes.submittedEmail }}
          </div>
        </template>
      </div>
    </BaseBubble>
    <BaseBubble
      v-if="hasAttachments"
      class="flex flex-col p-2.5 text-base leading-normal"
      data-bubble-name="text-attachments"
    >
      <AttachmentChips :attachments="attachments" class="gap-2" />
    </BaseBubble>
  </div>
</template>
